import type { ProviderAttachment, ProviderThread, ProviderThreadMessage } from "../providerMessagingTypes"
import { stripHealthieLinks } from "./healthieLinks"

// One provider conversation as the panel sees it, with every wire-shape difference already resolved.
// The list, the header and the composer read this shape; only the normalizers below ever touch a wire
// shape.
//
// The Carrot normalizers that used to sit beside these are gone: the Carrot side of /messages is the
// app's own again (src/components/messaging/**) and reads its own payload directly.

export type UnifiedThread = {
  /** Healthie's conversation id, opaque to us. */
  threadId: string
  /** Healthie's own title, or null. Never a participant list, never the preview — see the wire type. */
  subject: string | null
  /** Epoch ms. `0` when the source gave us nothing usable, so the thread sorts LAST, not first. */
  updatedAt: number
  unread: boolean
  isClosed: boolean
  preview: string | null
  /** Whether the composer should be offered at all. */
  canReply: boolean
  /** Who the conversation is with, for its header. Null when nobody is resolved — see the wire type. */
  providerName: string | null
  /** Their picture, or null. Absent far more often than present; see the wire type. */
  providerAvatarUrl: string | null
  /**
   * Their credential — "MD" — or null. Kept APART from `providerName`; the label is composed at
   * the point of display and the header's initials read the bare name. See the wire type.
   */
  providerQualifications: string | null
  /**
   * The OTHER people in the conversation, when `providerName` cannot name it on its own.
   *
   * A group thread resolves no single counterpart, so `providerName` is null; without this the row
   * and both headers render a bare date. Each entry keeps its name and credential APART for the
   * same reason `providerName`/`providerQualifications` do — `providerLabel` joins one person, and
   * `participantLabel` joins the list.
   *
   * Empty when the mirror sent none, which is also what an older BFF degrades to. Never undefined:
   * the wire's optionality stops at the normalizer, as it does for `providerQualifications`.
   */
  participants: Array<{ name: string; qualifications: string | null }>
  /**
   * How many other people are in the thread, UNCAPPED, where `participants` is capped by the
   * mirror. The difference is what renders as "+N". `0` when the wire sent nothing.
   */
  participantCount: number
}

export function fromProviderThread(thread: ProviderThread): UnifiedThread {
  return {
    threadId: thread.threadId,
    // Healthie's own title, or null when the conversation was never named — never a participant
    // list and never the preview; see the wire type. Carried through untouched: an untitled row
    // omits its subject LINE, which is the row's decision to make, not the normalizer's.
    subject: thread.subject,
    updatedAt: thread.updatedAt,
    unread: thread.unread,
    isClosed: thread.isClosed,
    preview: thread.preview,
    // There is no follow-up-thread mechanism on the provider side, so a closed conversation is
    // genuinely read-only — unlike a closed Carrot thread, whose composer stays up because replying
    // starts a follow-up. That asymmetry is why `canReply` exists rather than callers reading
    // `isClosed` themselves, and it is worth keeping now that only one side of it is left: the
    // components ask "may I reply?", which is the question they actually have.
    canReply: !thread.isClosed,
    // Carried through untouched, nulls included. Deciding what an absent name LOOKS like belongs to
    // the components — see `providerLabel`, and `ThreadPaneHeader`, which omits its heading entirely
    // rather than rendering an empty one.
    //
    // ⚠ `providerAvatarUrl` HAS NO READER IN THE SPA at all since CONAPP-277 deleted the header that
    // drew the picture. It is carried because it is on the wire, not because anything renders it; the
    // only guard left against an unusable value is the mirror's own (`renderableAvatarUrl` in
    // packages/convex), so a future reader has to re-establish that a value is an absolute http(s)
    // URL before pointing an `<img src>` at it.
    providerName: thread.providerName,
    providerAvatarUrl: thread.providerAvatarUrl,
    // Carried through SEPARATELY, never joined onto the name here. Composing the label is a
    // display decision (`providerLabel`), and the header's initials depend on this staying apart.
    //
    // `?? null` IS THE NORMALIZATION, not padding: the field is OPTIONAL on the wire so that a BFF
    // deployed ahead of the mirror degrades to "no credential" instead of failing the whole
    // response. That tolerance stops HERE — `UnifiedThread` keeps it `string | null`, so no
    // component has to distinguish "absent" from "none", and every `providerLabel` call site is
    // spared a third case.
    providerQualifications: thread.providerQualifications ?? null,
    // `?? []` and `?? 0` are the same normalization as the line above, for the same deploy-order
    // reason: both fields are OPTIONAL on the wire so a BFF running ahead of the mirror degrades to
    // "no participants" rather than failing the response. The tolerance stops here — a component
    // asking "is this a group?" gets an array and a number, never `undefined`.
    participants: thread.participants ?? [],
    participantCount: thread.participantCount ?? 0
  }
}

// One message in the open thread, normalized the same way.

export type UnifiedMessage = {
  /** React key / identity within one thread's rendered list. The provider gives a real message id. */
  id: string
  /**
   * Runs through `getParsedHtmlElements`, like every message body this package renders.
   *
   * ⚠ NOT byte-identical to the wire body: Healthie's own dead links are removed on the way in — see
   * `fromProviderMessage`.
   */
  body: string
  /** Epoch ms. */
  createdAt: number
  authoredByMember: boolean
  /** The author's display name, e.g. "Pat Smith". */
  authorLabel: string | null
  /**
   * The files on this message, ALWAYS AN ARRAY here — the wire field is optional and the normalizer
   * collapses its absence, so no component has to tell "absent" from "none".
   */
  attachments: ProviderAttachment[]
}

export function fromProviderMessage(message: ProviderThreadMessage): UnifiedMessage {
  return {
    id: message.messageId,
    // ⛔ HEALTHIE'S OWN LINKS COME OUT HERE, at the boundary where a provider's body enters the
    // package — not in the renderer, which stays a generic HTML renderer. `lib/healthieLinks.ts`
    // has the whole argument: the link Healthie injects for an "Attach file" send dead-ends on a
    // Healthie LOGIN PAGE, and leaving it in shows the member two links to one file, one of which
    // looks like Carrot is broken. The working one is rendered from `attachments` below.
    body: stripHealthieLinks(message.body),
    // Already epoch ms on the wire — the provider payload is normalized host-side.
    createdAt: message.createdAt,
    authoredByMember: message.authoredByMember,
    authorLabel: message.authorLabel,
    // `?? []` is the same normalization `participants` above documents, for the same two reasons:
    // the mirror OMITS the field on a message with no files, and a BFF deployed ahead of the mirror
    // omits it on every message. Both mean "no files", and neither reaches a component.
    attachments: message.attachments ?? []
  }
}
